import * as React from 'react';
import { Button, Loader, Select, TextInput } from 'components';
import { TStockTypeEnum } from 'api/stock/stock.types';
import { useAppDispatch, useAppSelector } from 'store/store';
import { getPhoneCasesListAsyncThunk } from 'store/phoneCases/phoneCases.action';
import { getStockListByTypeAsyncThunk } from 'store/stock/stock.action';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import {
  buildCheckoutLine,
  findStockLineByNameAndVariation,
} from './OrderLineItemModal.utils';
import { TOrderLineItemModal } from './OrderLineItemModal.types';
import './OrderLineItemModal.styled.scss';
import '../../../../AdminPanel.styles.scss';

export const OrderLineItemModal: TOrderLineItemModal = ({
  closeModal,
  initialLine,
  onConfirm,
}) => {
  const localize = useLocalize();
  const dispatch = useAppDispatch();

  const {
    phoneCasesList: { data: phoneCasesPayload, isLoading: isPhoneCasesLoading },
  } = useAppSelector((s) => s.phoneCases);

  const {
    stockListByType: { data: stockByTypePayload, isLoading: isStockLoading },
  } = useAppSelector((s) => s.stock);

  const products = phoneCasesPayload?.items ?? [];
  const stockRows = stockByTypePayload?.[TStockTypeEnum.PhoneCase] ?? [];

  const [productId, setProductId] = React.useState('');
  const [stockItemId, setStockItemId] = React.useState('');
  const [price, setPrice] = React.useState(0);
  const [quantity, setQuantity] = React.useState(1);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    dispatch(
      getPhoneCasesListAsyncThunk({
        queryParams: { page: 1, perPage: 1000 },
      })
    );
    dispatch(
      getStockListByTypeAsyncThunk({
        queryParams: { typeList: [TStockTypeEnum.PhoneCase] },
      })
    );
  }, [dispatch]);

  React.useEffect(() => {
    if (!initialLine) {
      setProductId('');
      setStockItemId('');
      setPrice(0);
      setQuantity(1);
      setSubmitError(null);
      return;
    }
    setProductId(initialLine.productId);
    setPrice(initialLine.price);
    setQuantity(initialLine.quantity);
    setStockItemId('');
    setSubmitError(null);
  }, [initialLine]);

  React.useEffect(() => {
    if (!initialLine?.stock?.[0] || stockRows.length === 0) {
      return;
    }
    const { name, variation } = initialLine.stock[0];
    const match = findStockLineByNameAndVariation(stockRows, name, variation);
    if (match) {
      setStockItemId(match.id);
    }
  }, [initialLine, stockRows]);

  const productOptions = React.useMemo(
    () =>
      products.map((p) => ({
        id: p.id,
        label: localize(p.name) as string,
      })),
    [products, localize]
  );

  const stockOptions = React.useMemo(
    () =>
      stockRows.map((s) => ({
        id: s.id,
        label: `${s.name} — ${s.variation ?? ''}`,
      })),
    [stockRows]
  );

  const handleProductChange = React.useCallback(
    (id: string) => {
      setProductId(id);
      const p = products.find((x) => x.id === id);
      if (p) {
        const nextPrice = p.newPrice ?? p.price;
        setPrice(typeof nextPrice === 'number' ? nextPrice : 0);
      }
    },
    [products]
  );

  const isLoading = isPhoneCasesLoading || isStockLoading;

  const handleSubmit = () => {
    setSubmitError(null);
    const product = products.find((p) => p.id === productId);
    const stockItem = stockRows.find((s) => s.id === stockItemId);
    if (!product) {
      setSubmitError(
        localize('validation.admin.orders.modal.product') as string
      );
      return;
    }
    if (!stockItem) {
      setSubmitError(localize('validation.admin.orders.modal.stock') as string);
      return;
    }
    if (quantity < 1 || !Number.isFinite(quantity)) {
      setSubmitError(
        localize('validation.admin.orders.quantity.min') as string
      );
      return;
    }
    if (price < 0 || !Number.isFinite(price)) {
      setSubmitError(localize('validation.admin.orders.price.min') as string);
      return;
    }
    onConfirm(buildCheckoutLine(product, stockItem, price, quantity));
    closeModal();
  };

  return (
    <div className="admin-order-line-modal">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="admin-form__field">
            <Select
              label={localize('admin.orders.form.modal.product') as string}
              value={productId}
              onChange={(e) =>
                handleProductChange(
                  String(
                    (e as React.ChangeEvent<{ value: string }>).target.value
                  )
                )
              }
              options={productOptions}
              required
            />
          </div>
          <div className="admin-form__field">
            <Select
              label={localize('admin.orders.form.modal.stock') as string}
              value={stockItemId}
              onChange={(e) =>
                setStockItemId(
                  String(
                    (e as React.ChangeEvent<{ value: string }>).target.value
                  )
                )
              }
              options={stockOptions}
              required
            />
          </div>
          <div className="admin-form__field-row admin-form__field-row--half">
            <div className="admin-form__field">
              <TextInput
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                label={
                  localize('admin.orders.detail.label.unitprice') as string
                }
                value={price}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  setPrice(Number.isNaN(n) ? 0 : n);
                }}
                required
              />
            </div>
            <div className="admin-form__field">
              <TextInput
                type="number"
                inputProps={{ min: 1, step: 1 }}
                label={localize('admin.orders.detail.label.quantity') as string}
                value={quantity}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setQuantity(Number.isNaN(n) ? 1 : n);
                }}
                required
              />
            </div>
          </div>
          {submitError ? (
            <p className="body2 admin-order-line-modal__error">{submitError}</p>
          ) : null}
          <div className="admin-order-line-modal__actions">
            <Button type="button" variant="outlined" onClick={closeModal}>
              {localize('admin.orders.form.modal.cancel')}
            </Button>
            <Button type="button" variant="contained" onClick={handleSubmit}>
              {initialLine
                ? localize('admin.orders.form.modal.confirm')
                : localize('admin.orders.form.modal.add')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderLineItemModal;
